import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import api from "../services/api";

const AuthContext = createContext(null);

// Check if running on HTTP (non-secure context)
const isHttpMode = window.location.protocol === "http:" && window.location.hostname !== "localhost";

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const msalIsAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // In HTTP mode, always authenticated with local admin
  const isAuthenticated = isHttpMode ? true : msalIsAuthenticated;

  const getAccessToken = useCallback(async () => {
    if (accounts.length === 0) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.error("Silent token acquisition failed:", error);
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        return response.accessToken;
      } catch (popupError) {
        console.error("Interactive token acquisition failed:", popupError);
        return null;
      }
    }
  }, [instance, accounts]);

  const lookupUserRole = useCallback(async (email) => {
    // Fallback: look up user role from resources collection by email
    console.log("lookupUserRole called for:", email);
    const MANAGEMENT_ROLES = ["Management", "Project Management"];
    
    try {
      const response = await api.get("/resources");
      console.log("Resources API response success:", response.data.success, "count:", response.data.data?.length);
      if (response.data.success && response.data.data) {
        const resources = response.data.data;
        // Case-insensitive email match
        const userResource = resources.find(r => 
          r.fields?.email?.toLowerCase() === email?.toLowerCase()
        );
        
        if (userResource) {
          // Support both old 'role' string and new 'roles' array
          const roles = userResource.fields?.roles || [];
          const roleString = userResource.fields?.role || roles.join(", ") || "";
          const isManagement = roles.some(r => MANAGEMENT_ROLES.includes(r)) || 
                              userResource.fields?.is_management === true;
          const isAdmin = roles.includes("Admin");
          
          console.log("Found user resource:", userResource.fields?.name, "roles:", roles, "isManagement:", isManagement);
          
          return {
            role: roleString,
            roles: roles,
            resource_id: userResource.id,
            resource_name: userResource.fields?.name,
            is_management: isManagement,
            is_admin: isAdmin
          };
        } else {
          console.log("No matching resource found for email:", email);
          // Log first few emails to debug
          console.log("Sample emails in resources:", resources.slice(0, 5).map(r => r.fields?.email));
        }
      }
    } catch (err) {
      console.error("Failed to lookup user role:", err);
    }
    return { role: "user", roles: [], resource_id: null, is_management: false, is_admin: false };
  }, []);

  const syncUserWithBackend = useCallback(async (accessToken) => {
    const account = accounts.length > 0 ? accounts[0] : null;
    const email = account?.username;
    
    console.log("syncUserWithBackend called for email:", email);
    
    try {
      const response = await api.post("/auth/login", {}, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      if (response.data.success) {
        console.log("Backend auth sync successful:", response.data.user);
        console.log("is_management from backend:", response.data.user?.is_management);
        // Store the token if provided
        if (response.data.token) {
          localStorage.setItem('sso_backend_token', response.data.token);
        }
        setUser(response.data.user);
        return;
      }
    } catch (error) {
      console.error("Backend auth sync failed:", error.response?.status, error.response?.data);
    }
    
    // Fallback: Use MSAL account info + lookup role from resources, then get a backend token
    if (account) {
      console.log("Using fallback auth with resources lookup for:", email);
      const roleInfo = await lookupUserRole(email);
      console.log("Role info from resources lookup:", roleInfo);
      
      // Request a backend token for this SSO user
      try {
        const tokenResponse = await api.post("/auth/sso-token", {
          email: email,
          display_name: account.name
        });
        
        if (tokenResponse.data.success) {
          console.log("Got backend token for SSO user");
          localStorage.setItem('sso_backend_token', tokenResponse.data.token);
          setUser(tokenResponse.data.user);
          return;
        }
      } catch (tokenError) {
        console.error("Failed to get backend token:", tokenError);
      }
      
      // Final fallback - set user without backend token (read-only access)
      const userData = {
        email: email,
        displayName: account.name,
        display_name: account.name,
        entraId: account.localAccountId,
        role: roleInfo.role,
        resource_id: roleInfo.resource_id,
        resource_name: roleInfo.resource_name,
        is_management: roleInfo.is_management,
        is_admin: roleInfo.is_admin
      };
      console.log("Setting user with is_management (no backend token):", userData.is_management);
      setUser(userData);
    }
  }, [accounts, lookupUserRole]);

  useEffect(() => {
    const initAuth = async () => {
      // HTTP mode bypass - set local admin user
      if (isHttpMode) {
        setUser({
          email: "admin@local",
          displayName: "Local Admin",
          display_name: "Local Admin",
          role: "admin",
        });
        setLoading(false);
        return;
      }
      
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      if (isAuthenticated && accounts.length > 0) {
        const token = await getAccessToken();
        if (token) {
          // Store token for API requests
          localStorage.setItem('msal_access_token', token);
          await syncUserWithBackend(token);
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, [isAuthenticated, accounts, inProgress, getAccessToken, syncUserWithBackend]);

  const login = async (usePopup = false) => {
    // HTTP mode - no login needed
    if (isHttpMode) {
      setUser({
        email: "admin@local",
        displayName: "Local Admin",
        display_name: "Local Admin",
        role: "admin",
        is_management: true,
      });
      return;
    }
    
    try {
      setError(null);
      
      if (usePopup) {
        // Popup method - works better with some browser configurations
        const response = await instance.loginPopup({
          ...loginRequest,
          prompt: 'select_account'
        });
        if (response?.account) {
          instance.setActiveAccount(response.account);
          const token = await getAccessToken();
          if (token) {
            await syncUserWithBackend(token);
          }
        }
      } else {
        // Redirect method - default, better for mobile and self-signed certs
        await instance.loginRedirect({
          ...loginRequest,
          prompt: 'select_account'
        });
      }
    } catch (error) {
      console.error("Login failed:", error);
      setError(error.message || "Login failed. Try using popup login instead.");
    }
  };

  const logout = async () => {
    // Clear all tokens
    localStorage.removeItem("backdoor_user");
    localStorage.removeItem("backdoor_token");
    localStorage.removeItem("msal_access_token");
    localStorage.removeItem("sso_backend_token");
    
    // HTTP mode - just clear user and reload
    if (isHttpMode) {
      setUser(null);
      window.location.reload();
      return;
    }
    
    try {
      setUser(null);
      await instance.logoutPopup({
        account: accounts[0],
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Backdoor admin login (bypasses SSO)
  const backdoorLogin = async (username, password) => {
    try {
      const response = await api.post("/auth/backdoor", { username, password });
      if (response.data.success) {
        const backdoorUser = response.data.user;
        const token = response.data.token;
        setUser(backdoorUser);
        localStorage.setItem("backdoor_user", JSON.stringify(backdoorUser));
        localStorage.setItem("backdoor_token", token);
        return { success: true };
      }
      return { success: false, error: "Login failed" };
    } catch (error) {
      console.error("Backdoor login failed:", error);
      return { success: false, error: error.response?.data?.detail || "Invalid credentials" };
    }
  };

  // Check for existing backdoor session on mount - use state initializer instead of effect
  const [backdoorInitialized] = useState(() => {
    const savedBackdoorUser = localStorage.getItem("backdoor_user");
    if (savedBackdoorUser) {
      try {
        const parsed = JSON.parse(savedBackdoorUser);
        // Set user state through initial value pattern
        return parsed;
      } catch (e) {
        localStorage.removeItem("backdoor_user");
      }
    }
    return null;
  });
  
  // Initialize user from backdoor session if not already set
  useEffect(() => {
    if (backdoorInitialized && !user && !loading) {
      setUser(backdoorInitialized);
    }
  }, [backdoorInitialized, loading]);

  // Debug: Log whenever user or canEdit changes
  const canEdit = user?.is_management === true || user?.is_admin === true;
  
  useEffect(() => {
    if (user) {
      console.log("AuthContext user state:", {
        email: user.email,
        role: user.role,
        is_management: user.is_management,
        is_admin: user.is_admin,
        canEdit: canEdit
      });
    }
  }, [user, canEdit]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: isAuthenticated || !!localStorage.getItem("backdoor_user"),
    login,
    logout,
    backdoorLogin,
    getAccessToken,
    // Helper for permission checks - management users can edit
    canEdit: canEdit,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
